import {
  CircleLoader,
  LoaderSizes,
  brandfolderColorPalette,
} from '@brandfolder/react';
import { Trans, plural, t } from '@lingui/macro';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { ImageOverlay } from '../common/ImageOverlay';
import { Pagination } from '../common/Pagination';
import { PeopleTagsDropdown } from '../common/PeopleTagsDropdown';
import { PER_PAGE_COUNT } from '../constants';
import {
  PersonDetails,
  PersonDetailsParamKeys,
} from '../customPeopleTagTypes';
import { useFetchPersonDetailsData } from '../fetch';
import { usePersonDetailsQueryState } from '../hooks/usePersonDetailsQueryState';

import { PersonDetailsHeader } from './PersonDetailsHeader';

const PersonDetailsPageWrapper = styled.div`
  margin: 40px auto;
  max-width: 1608px;
  width: 93%;

  .bf-loader__circle {
    height: 50vh;
  }
`;

const ActionBarWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  padding-bottom: 36px;

  .heading {
    color: ${brandfolderColorPalette.neutrals[500]};
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
  }

  .dropdown-button,
  ul#sort-dropdown-list {
    width: 244px;
    z-index: 2;
  }
`;

const MatchingPhotosWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: -20px;

  > * {
    margin: 0 20px 20px 0;
  }

  img {
    cursor: pointer;
    max-height: 252px;
  }
`;

interface PersonDetailsPageProps {
  orgApiKey: string;
}

export const PersonDetailsPage: FunctionComponent<PersonDetailsPageProps> = ({ orgApiKey }) => {
  const [state, setState] = usePersonDetailsQueryState();
  const { personDetailsData, personDetailsLoading } = useFetchPersonDetailsData(orgApiKey);
  const perPage = PER_PAGE_COUNT;

  const setSortByState = (key: string): void => {
    setState({ [PersonDetailsParamKeys.Sort]: key });
  };

  return (
    <PersonDetailsPageWrapper>
      <PersonDetailsHeader thumbnail={personDetailsData?.data[0]?.gcs_path} />
      <ActionBarWrapper>
        <span className="heading">
          <Trans>MATCHING PHOTOS</Trans>
        </span>
        <PeopleTagsDropdown
          selectedValue={state.sort}
          setSelectedValue={setSortByState}
          type="sort"
        />
      </ActionBarWrapper>
      <Pagination
        page={Number(state.page) || 1}
        perPage={perPage}
        setPage={setState}
        text={plural(personDetailsData?.meta.total_count, {
          one: 'photo',
          other: 'photos',
        })}
        total={personDetailsData?.meta.total_count}
      />
      {personDetailsLoading ? (
        <CircleLoader label="person-details-data" size={LoaderSizes.Large} />
      ) : (
        <MatchingPhotosWrapper>
          {!!personDetailsData?.data.length &&
            personDetailsData?.data.map((person: PersonDetails) => {
              return (
                <div key={`${person.match_face_id}-${person.asset_id}`}>
                  <ImageOverlay key={person.asset_id} buttonText={t`View`}>
                    <img alt="matching" src={person.gcs_path} />
                  </ImageOverlay>
                </div>
              );
            })}
        </MatchingPhotosWrapper>
      )}
    </PersonDetailsPageWrapper>
  );
};
